import { useMemo, useState } from "react";
import { Boxes, DollarSign, MapPinned, ShieldCheck, Truck, Wrench } from "lucide-react";

const formatCurrency = (value, currency = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(value || 0);

const templateFor = (businessType) => ({
  car_wash: { zoneLabel: "Bays", sessionLabel: "Wash Sessions", membershipLabel: "Loyalty Cards" },
  campsite: { zoneLabel: "Units / Stands", sessionLabel: "Guest Activity", membershipLabel: "Guest Accounts" },
  hotel: { zoneLabel: "Rooms / Areas", sessionLabel: "Service / Stay Records", membershipLabel: "Guest Accounts" },
  automotive_parts: { zoneLabel: "Counters / Stock Areas", sessionLabel: "Sales / Fulfillment", membershipLabel: "Customer Accounts" },
  manufacturing: { zoneLabel: "Lines / Areas", sessionLabel: "Operational Runs", membershipLabel: "Accounts" },
  general_ops: { zoneLabel: "Zones / Areas", sessionLabel: "Operational Sessions", membershipLabel: "Accounts" },
}[businessType] || { zoneLabel: "Zones / Areas", sessionLabel: "Operational Sessions", membershipLabel: "Accounts" });

export const OpsSiteDetail = ({
  site,
  business,
  procurement = [],
  suppliers = [],
  assets = [],
  zones = [],
  services = [],
  sessions = [],
  payments = [],
  memberships = [],
  maintenance = [],
  inventory = [],
  onBack,
  onCreateSupplier,
  onCreateProcurement,
  onCreateAsset,
  onCreateZone,
  onCreateService,
  onCreateSession,
  onCreatePayment,
  onCreateMembership,
  onCreateMaintenance,
  onCreateInventoryItem,
}) => {
  const [tab, setTab] = useState("procurement");
  const labels = templateFor(business?.business_type || "general_ops");
  const siteProcurement = procurement.filter((item) => item.site_id === site?.id);
  const siteAssets = assets.filter((item) => item.site_id === site?.id);
  const siteZones = zones.filter((item) => item.site_id === site?.id);
  const siteSessions = sessions.filter((item) => item.site_id === site?.id);
  const sitePayments = payments.filter((item) => item.site_id === site?.id);
  const siteMemberships = memberships.filter((item) => item.site_id === site?.id);
  const siteMaintenance = maintenance.filter((item) => item.site_id === site?.id);
  const siteInventory = inventory.filter((item) => item.site_id === site?.id);
  const revenue = useMemo(() => sitePayments.reduce((sum, item) => sum + Number(item.amount || 0), 0), [sitePayments]);

  const addRecord = async (kind) => {
    if (kind === "supplier") {
      const name = window.prompt("Supplier name");
      if (name) await onCreateSupplier?.({ business_id: business.id, name, supplier_type: "", contact_name: "", email: "", phone: "", payment_terms: "", status: "Active", notes: "" });
      return;
    }
    if (kind === "procurement") {
      const title = window.prompt("Procurement request title");
      if (title) await onCreateProcurement?.({ site_id: site.id, title, category: "", sub_category: "", requested_by: "", status: "Draft", needed_by: null, notes: "" });
      return;
    }
    if (kind === "asset") {
      const name = window.prompt("Asset name");
      if (name) await onCreateAsset?.({ site_id: site.id, name, category: "", sub_category: "", serial_number: "", supplier_id: "", zone_id: "", purchase_date: null, warranty_expiry: null, status: "Active", last_service_date: null, next_service_date: null, notes: "", metadata: {} });
      return;
    }
    if (kind === "zone") {
      const name = window.prompt(`${labels.zoneLabel} name`);
      if (name) await onCreateZone?.({ site_id: site.id, name, zone_type: "", status: "Active", capacity: 0, notes: "", metadata: {} });
      return;
    }
    if (kind === "service") {
      const name = window.prompt("Service name");
      if (name) await onCreateService?.({ site_id: site.id, name, service_code: "", service_type: "", duration_minutes: 0, base_price: 0, status: "Active", metadata: {} });
      return;
    }
    if (kind === "session") {
      const customer_name = window.prompt(`${labels.sessionLabel} customer/reference`);
      if (customer_name) await onCreateSession?.({ site_id: site.id, customer_name, zone_id: "", service_id: "", operator_name: "", start_time: new Date().toISOString(), end_time: null, status: "Open", gross_amount: 0, payment_status: "Pending", membership_id: "", reference_code: "", notes: "", metadata: {} });
      return;
    }
    if (kind === "payment") {
      const amount = window.prompt("Payment amount");
      if (amount) await onCreatePayment?.({ site_id: site.id, service_session_id: "", payment_method: "cash", reference_number: "", amount: Number(amount), currency: site.currency || "USD", status: "Completed", points_earned: 0, points_redeemed: 0, paid_at: new Date().toISOString() });
      return;
    }
    if (kind === "membership") {
      const member_name = window.prompt(`${labels.membershipLabel} name`);
      if (member_name) await onCreateMembership?.({ site_id: site.id, card_number: crypto.randomUUID().slice(0, 8).toUpperCase(), member_name, phone: "", email: "", points_balance: 0, stored_value_balance: 0, status: "Active", issued_at: new Date().toISOString(), expires_at: null, metadata: {} });
      return;
    }
    if (kind === "maintenance") {
      const title = window.prompt("Maintenance work order title");
      if (title) await onCreateMaintenance?.({ site_id: site.id, asset_id: "", title, maintenance_type: "", priority: "Medium", status: "Open", reported_by: "", assigned_to: "", scheduled_date: null, completed_date: null, downtime_minutes: 0, cost: 0, notes: "" });
      return;
    }
    if (kind === "inventory") {
      const name = window.prompt("Inventory item name");
      if (name) await onCreateInventoryItem?.({ site_id: site.id, name, category: "", sub_category: "", sku: "", unit: "pcs", quantity_on_hand: 0, reorder_level: 0, preferred_supplier_id: "", last_unit_cost: 0, status: "Active", item_role: "consumable", sale_price: 0, metadata: {} });
    }
  };

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <button className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500" onClick={onBack} type="button">Back to {business?.name || "Business"}</button>
            <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-zinc-900">{site?.name}</h1>
            <p className="mt-2 text-sm text-zinc-500">{site?.site_type || "general_site"} · {site?.location || "No location"} · Manager: {site?.manager_name || "Unassigned"}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric icon={<MapPinned size={16} />} label={labels.zoneLabel} value={siteZones.length} />
            <Metric icon={<Truck size={16} />} label="Suppliers" value={suppliers.length} />
            <Metric icon={<DollarSign size={16} />} label="Revenue" value={formatCurrency(revenue, site?.currency || "USD")} />
            <Metric icon={<Boxes size={16} />} label="Inventory" value={siteInventory.length} />
          </div>
        </div>
      </section>

      <section className="flex flex-wrap gap-2">
        {[["procurement", "Procurement"], ["suppliers", "Suppliers"], ["assets", "Assets"], ["operations", labels.sessionLabel], ["payments", "Payments"], ["maintenance", "Maintenance"], ["inventory", "Inventory"]].map(([key, label]) => (
          <button className={tab === key ? "btn-primary" : "btn-secondary"} key={key} onClick={() => setTab(key)} type="button">{label}</button>
        ))}
      </section>

      {tab === "procurement" ? <Section title="RFQ / Procurement" actionLabel="Add Request" onAction={() => addRecord("procurement")} items={siteProcurement} renderItem={(item) => (
        <div>
          <div className="flex items-start justify-between gap-4">
            <div><div className="font-semibold text-zinc-900">{item.title}</div><div className="mt-1 text-sm text-zinc-500">{item.category || "General"} · {item.status || "Draft"}</div></div>
            <div className="text-sm text-zinc-500">{item.lines?.length || 0} lines</div>
          </div>
        </div>
      )} /> : null}

      {tab === "suppliers" ? <Section title="Suppliers" actionLabel="Add Supplier" onAction={() => addRecord("supplier")} items={suppliers} renderItem={(item) => <div><div className="font-semibold text-zinc-900">{item.name}</div><div className="mt-1 text-sm text-zinc-500">{item.supplier_type || "General supplier"} · {item.contact_name || "No contact"}</div></div>} /> : null}
      {tab === "assets" ? <Section title="Assets" actionLabel="Add Asset" onAction={() => addRecord("asset")} items={siteAssets} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.name}</div><div className="mt-1 text-sm text-zinc-500">{item.category || "General"} · {item.status || "Active"}</div></div><ShieldCheck size={16} className="text-zinc-400" /></div>} /> : null}

      {tab === "operations" ? <div className="grid gap-6 xl:grid-cols-2">
        <Section title={labels.zoneLabel} actionLabel="Add Zone" onAction={() => addRecord("zone")} items={siteZones} renderItem={(item) => <div><div className="font-semibold text-zinc-900">{item.name}</div><div className="mt-1 text-sm text-zinc-500">{item.zone_type || "General"} · Capacity {item.capacity || 0}</div></div>} />
        <Section title={labels.sessionLabel} actionLabel="Add Session" onAction={() => addRecord("session")} items={siteSessions} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.customer_name || item.reference_code || "Unnamed session"}</div><div className="mt-1 text-sm text-zinc-500">{item.status || "Open"} · {item.payment_status || "Pending"}</div></div><div className="text-sm text-zinc-600">{formatCurrency(item.gross_amount || 0, site?.currency || "USD")}</div></div>} />
      </div> : null}

      {tab === "payments" ? <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Payments" actionLabel="Add Payment" onAction={() => addRecord("payment")} items={sitePayments} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.payment_method || "Payment"}</div><div className="mt-1 text-sm text-zinc-500">{item.status || "Pending"} · {item.reference_number || "No reference"}</div></div><div className="text-sm text-zinc-600">{formatCurrency(item.amount || 0, item.currency || site?.currency || "USD")}</div></div>} />
        <Section title={labels.membershipLabel} actionLabel="Add Account" onAction={() => addRecord("membership")} items={siteMemberships} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.member_name}</div><div className="mt-1 text-sm text-zinc-500">{item.card_number} · {item.status || "Active"}</div></div><div className="text-right text-sm text-zinc-600"><div>{item.points_balance || 0} pts</div><div>{formatCurrency(item.stored_value_balance || 0, site?.currency || "USD")}</div></div></div>} />
      </div> : null}

      {tab === "maintenance" ? <Section title="Maintenance" actionLabel="Add Work Order" onAction={() => addRecord("maintenance")} items={siteMaintenance} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.title}</div><div className="mt-1 text-sm text-zinc-500">{item.priority || "Medium"} · {item.status || "Open"} · {item.logs?.length || 0} logs</div></div><Wrench size={16} className="text-zinc-400" /></div>} /> : null}
      {tab === "inventory" ? <Section title="Inventory" actionLabel="Add Inventory Item" onAction={() => addRecord("inventory")} items={siteInventory} renderItem={(item) => <div className="flex items-start justify-between gap-4"><div><div className="font-semibold text-zinc-900">{item.name}</div><div className="mt-1 text-sm text-zinc-500">{item.sku || "No SKU"} · {item.item_role || "consumable"} · {item.category || "General"}</div></div><div className="text-right text-sm text-zinc-600"><div>{item.quantity_on_hand} {item.unit}</div><div>Reorder {item.reorder_level}</div></div></div>} /> : null}
    </div>
  );
};

const Metric = ({ icon, label, value }) => (
  <div className="border border-zinc-200 bg-white px-4 py-3">
    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-[0.18em] text-zinc-500"><span>{label}</span>{icon}</div>
    <div className="mt-2 text-xl font-black text-zinc-900">{value}</div>
  </div>
);

const Section = ({ title, actionLabel, onAction, items = [], renderItem }) => (
  <section className="card p-6">
    <div className="mb-4 flex items-center justify-between gap-4">
      <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">{title}</h2>
      {onAction ? <button className="btn-primary" onClick={onAction} type="button">{actionLabel}</button> : null}
    </div>
    <div className="space-y-3">
      {items.length === 0 ? <p className="text-sm text-zinc-400">Nothing here yet.</p> : items.map((item) => <div className="border border-zinc-200 bg-white p-4" key={item.id}>{renderItem(item)}</div>)}
    </div>
  </section>
);
